import classNames from "classnames";
import { Fragment, useEffect, useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useDraggable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS, Transform } from "@dnd-kit/utilities";
import { DeleteIcon, DragHandleIcon } from "../../icons";
import JourneyDropSlot from "./JourneyDropSlot";
import { Journey } from "./journeyPlanner.types";
import "../../lists/ListRowActions.scss";
import SortableJourneyItem from "./SortableJourneyItem";
import { useJourneyPlanner } from "./JourneyPlannerContext";

interface JourneyCardProps {
  defaultJourneyName: string;
  journey: Journey;
  setJourneyRef: (node: HTMLElement | null) => void;
}

function JourneyCard({
  defaultJourneyName,
  journey,
  setJourneyRef,
}: JourneyCardProps) {
  const {
    activeDrag,
    handleRemoveJourneyItem,
    replaceJourney,
    setJourneyPendingDelete,
    zoomLevel,
  } = useJourneyPlanner();
  const [journeyName, setJourneyName] = useState(journey.Name || "");

  useEffect(() => {
    setJourneyName(journey.Name || "");
  }, [journey.Name]);

  const { attributes, listeners, setNodeRef, transform } =
    useDraggable({
      id: `journey-${journey.Id}`,
      data: {
        type: "journey-card",
        journeyId: journey.Id,
        label: journey.Name,
      },
    });
  const isDraggingJourney =
    activeDrag?.type === "journey-card" &&
    activeDrag.journeyId === journey.Id;
  const isReorderingDifferentJourney =
    activeDrag?.type === "journey-item" &&
    activeDrag.journeyId !== journey.Id;
  const hideDropSlots =
    activeDrag?.type === "journey-card" ||
    isReorderingDifferentJourney;
  const scaledTransform = getBoundedJourneyTransform(
    transform,
    journey,
    zoomLevel
  );
  const style = {
    left: `${journey.X ?? 0}px`,
    top: `${journey.Y ?? 0}px`,
    transform: CSS.Translate.toString(scaledTransform),
  };

  function handleNodeRef(node: HTMLElement | null) {
    setNodeRef(node);
    setJourneyRef(node);
  }

  function handleJourneyNameBlur() {
    const trimmedName = journeyName.trim();
    const nextName = trimmedName || defaultJourneyName;

    if (nextName === journey.Name) {
      setJourneyName(nextName);
      return;
    }

    replaceJourney({
      ...journey,
      Name: nextName,
    });
    setJourneyName(nextName);
  }

  function renderDropSlot(insertIndex: number, className = "my-2") {
    return (
      <JourneyDropSlot
        activeTypes={["sidebar-automation", "journey-item"]}
        activeWhen={(activeData) => {
          if (activeData?.type !== "journey-item") {
            return true;
          }

          return activeData.journeyId === journey.Id;
        }}
        className={classNames(className, {
          invisible: hideDropSlots,
        })}
        data={{
          type: "journey-drop",
          journeyId: journey.Id,
          insertIndex,
        }}
        id={`journey-drop-${journey.Id}-${insertIndex}`}
      />
    );
  }

  return (
    <div
      className={classNames(
        "position-absolute surface-grid-card",
        {
          "edit-element is-active": isDraggingJourney,
        }
      )}
      ref={handleNodeRef}
      style={style}
    >
      <div className="card surface-grid-journey-card">
        <h3 className="card-header">
        <div className="d-flex justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center flex-grow-1 gap-2">
            <div
              className="drag-handle d-inline-flex align-items-center justify-content-center flex-shrink-0 fs-6"
              title="Move journey"
              {...attributes}
              {...listeners}
            >
              <FontAwesomeIcon icon={DragHandleIcon} />
            </div>
            <input
              className="form-control flex-grow-1"
              id={`journey-name-${journey.Id}`}
              name={`journey-name-${journey.Id}`}
              onBlur={handleJourneyNameBlur}
              onChange={(event) =>
                setJourneyName(event.target.value)
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
              }}
              onPointerDown={(event) => event.stopPropagation()}
              type="text"
              value={journeyName}
            />
            <button
              className="list-row-action flex-shrink-0 hover-text-danger"
              onClick={() => setJourneyPendingDelete(journey)}
              onPointerDown={(event) => event.stopPropagation()}
              title={`Delete ${journey.Name || defaultJourneyName}`}
              aria-label={`Delete ${journey.Name || defaultJourneyName}`}
              type="button"
            >
              <FontAwesomeIcon icon={DeleteIcon} />
            </button>
          </div>
        </div>
        </h3>
        <div className="card-body rounded-bottom">
          <SortableContext
            items={journey.Items.map(
              (item) => `journey-item-${item.Id}`
            )}
            strategy={verticalListSortingStrategy}
          >
            {journey.Items.length === 0 ? (
              <>
                {renderDropSlot(0, "mb-2")}
                <p className="text-muted mb-0">
                  Drag an automation into this journey.
                </p>
              </>
            ) : (
              <>
                {renderDropSlot(0, "mb-2")}
                {journey.Items.map((item, index) => (
                  <Fragment key={item.Id}>
                    <SortableJourneyItem
                      canDrag={journey.Items.length > 1}
                      item={item}
                      index={index}
                      journeyId={journey.Id}
                      onRemove={() =>
                        handleRemoveJourneyItem(journey.Id, item.Id)
                      }
                    />
                    {renderDropSlot(index + 1)}
                  </Fragment>
                ))}
              </>
            )}
          </SortableContext>
        </div>
      </div>
    </div>
  );
}

function getBoundedJourneyTransform(
  transform: Transform | null,
  journey: Journey,
  zoomLevel: number
) {
  if (!transform) {
    return transform;
  }

  const safeZoomLevel = zoomLevel || 1;
  const currentX = journey.X ?? 0;
  const currentY = journey.Y ?? 0;
  const draggedPosition = {
    x: Math.max(0, currentX + transform.x / safeZoomLevel),
    y: Math.max(0, currentY + transform.y / safeZoomLevel),
  };

  return {
    ...transform,
    x: draggedPosition.x - currentX,
    y: draggedPosition.y - currentY,
  };
}

export default JourneyCard;
